import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, lighten } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  ArrowOutwardRounded as ArrowOutwardRoundedIcon,
  BugReportOutlined as BugReportOutlinedIcon,
  HubOutlined as HubOutlinedIcon,
  LanOutlined as LanOutlinedIcon,
  LinkOutlined as LinkOutlinedIcon,
  PublicOutlined as PublicOutlinedIcon,
  ShieldOutlined as ShieldOutlinedIcon,
  StorageOutlined as StorageOutlinedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from '@mui/icons-material';

import Nav from './nav';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';

const VERIFY_THRESHOLD = 25;

const getGrade = (score) => {
  if (score == null) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};

const gradePalette = {
  'A+': '#2e7d32',
  'A-': '#2e7d32',
  B: '#1565c0',
  C: '#ef6c00',
  D: '#ed6c02',
  F: '#d32f2f',
  '?': '#90a4ae',
};

const severityMeta = {
  critical: { label: 'Critical', color: '#7b1fa2', weight: 6, order: 5 },
  high: { label: 'High', color: '#d32f2f', weight: 4, order: 4 },
  medium: { label: 'Medium', color: '#ef6c00', weight: 3, order: 3 },
  low: { label: 'Low', color: '#1976d2', weight: 2, order: 2 },
  info: { label: 'Info', color: '#2e7d32', weight: 1, order: 1 },
};

const toneMap = {
  blue: { color: PRIMARYCOLOR, bg: alpha(PRIMARYCOLOR, 0.08) },
  red: { color: '#d32f2f', bg: alpha('#d32f2f', 0.08) },
  amber: { color: '#ef6c00', bg: alpha('#ef6c00', 0.08) },
  green: { color: '#2e7d32', bg: alpha('#2e7d32', 0.08) },
  slate: { color: '#455a64', bg: alpha('#455a64', 0.08) },
  purple: { color: '#7b1fa2', bg: alpha('#7b1fa2', 0.08) },
};

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatPct = (value) => `${Math.round(num(value, 0) * 100)}%`;

const severityKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized.includes('critical')) return 'critical';
  if (normalized.includes('high')) return 'high';
  if (normalized.includes('medium')) return 'medium';
  if (normalized.includes('low')) return 'low';
  return 'info';
};

const getAttackFindings = (attack) =>
  Array.isArray(attack?.exploit_results?.findings) ? attack.exploit_results.findings : [];

const safeParseList = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((entry) => String(entry).trim()).filter(Boolean);
        }
      } catch {
        // continue to fallback parsing
      }
    }

    if (trimmed.includes(',')) {
      return trimmed
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
    }

    return [trimmed];
  }

  return [];
};

const parseImpactedAssets = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return [...new Set(value.map((entry) => String(entry).trim()).filter(Boolean))];
  }

  const str = String(value).trim();
  if (!str) return [];

  const looksBracketed =
    (str.startsWith('[') && str.endsWith(']')) ||
    (str.startsWith('(') && str.endsWith(')'));

  if (looksBracketed) {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) {
        return [...new Set(parsed.map((entry) => String(entry).trim()).filter(Boolean))];
      }
    } catch {
      // continue with fallback parsing
    }

    const quoted = [];
    const regex = /'([^']*)'|"([^"]*)"/g;
    let match;
    while ((match = regex.exec(str)) !== null) {
      const hit = (match[1] ?? match[2] ?? '').trim();
      if (hit) quoted.push(hit);
    }
    if (quoted.length) {
      return [...new Set(quoted)];
    }

    const inner = str.slice(1, -1).trim();
    return [...new Set(inner.split(/,|\s+and\s+/gi).map((entry) => entry.trim()).filter(Boolean))];
  }

  return [...new Set(str.split(/[;|]|,|\s+and\s+/gi).map((entry) => entry.trim()).filter(Boolean))];
};

const normalizeTechToken = (value) => {
  const token = String(value || '').trim();
  if (!token) return '';
  if (!token.includes(':')) return token;

  const [leftRaw, rightRaw] = token.split(':', 2);
  const left = String(leftRaw || '').trim();
  const right = String(rightRaw || '').trim();

  const genericKeys = new Set([
    'server',
    'web server',
    'http server',
    'framework',
    'platform',
    'powered by',
    'technology',
    'stack',
    'vendor',
    'product',
    'service',
  ]);

  if (genericKeys.has(left.toLowerCase()) && right) {
    return right;
  }

  return left || token;
};

const normalizeLookupKey = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    return new URL(raw).hostname.toLowerCase();
  } catch {
    return raw
      .replace(/^https?:\/\//i, '')
      .replace(/\/.*$/, '')
      .replace(/:\d+$/, '')
      .toLowerCase();
  }
};

const getDomain = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return 'Unknown domain';
  }
};

const cardShellSx = {
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(PRIMARYCOLOR, 0.09),
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.06)',
  bgcolor: '#fff',
};

const SectionCard = ({ title, subtitle, action, children, sx }) => (
  <Card sx={{ ...cardShellSx, p: 3, ...sx }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Card>
);

const MetricCard = ({ label, value, caption, icon: Icon, tone = 'blue' }) => {
  const toneStyle = toneMap[tone] || toneMap.blue;

  return (
    <Card sx={{ ...cardShellSx, p: 2.2, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            bgcolor: toneStyle.bg,
            color: toneStyle.color,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', letterSpacing: 0.3 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1, color: '#10243E', mt: 0.35 }}>
            {value}
          </Typography>
          {caption ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.7, lineHeight: 1.45 }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Card>
  );
};

const SeverityCountPills = ({ counts, prefix }) => {
  const items = ['critical', 'high', 'medium', 'low', 'info'];

  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      {items.map((key) => (
        <Chip
          key={`${prefix}-${key}`}
          size="small"
          label={`${severityMeta[key].label} ${num(counts?.[key], 0)}`}
          sx={{
            height: 26,
            bgcolor: alpha(severityMeta[key].color, 0.1),
            color: severityMeta[key].color,
            fontWeight: 800,
          }}
        />
      ))}
    </Stack>
  );
};

const BarList = ({ rows, color, emptyLabel, secondaryKey = 'value' }) => {
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  const maxValue = Math.max(1, ...rows.map((row) => num(row.value, 0)));

  return (
    <Stack spacing={1.35}>
      {rows.map((row) => {
        const percent = (num(row.value, 0) / maxValue) * 100;
        return (
          <Box key={row.label}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: '#10243E', minWidth: 0 }}
                noWrap
                title={row.label}
              >
                {row.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {row[secondaryKey]}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                mt: 0.7,
                height: 8,
                borderRadius: 999,
                bgcolor: alpha(color, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: color,
                },
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

const FlowStat = ({ label, value, helper, tone }) => {
  const toneStyle = toneMap[tone] || toneMap.blue;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        bgcolor: toneStyle.bg,
        border: '1px solid',
        borderColor: alpha(toneStyle.color, 0.14),
        height: '100%',
      }}
    >
      <Typography variant="caption" sx={{ color: toneStyle.color, fontWeight: 800, letterSpacing: 0.35 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 950, color: '#10243E', mt: 0.35 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.7, lineHeight: 1.45 }}>
        {helper}
      </Typography>
    </Box>
  );
};

const scoreBreakpoints = [
  { label: 'Below verify threshold', key: 'low', color: '#90a4ae' },
  { label: 'Queued for review', key: 'mid', color: '#1976d2' },
  { label: 'Elevated attack paths', key: 'high', color: '#ef6c00' },
  { label: 'Likely attack paths', key: 'vhigh', color: '#d32f2f' },
];

const RiskBreakdownCard = ({ pentestCounts, cveCounts }) => {
  const totalPentest = Object.values(pentestCounts || {}).reduce((acc, value) => acc + num(value, 0), 0);
  const totalCves = Object.values(cveCounts || {}).reduce((acc, value) => acc + num(value, 0), 0);
  const maxValue = Math.max(
    1,
    ...Object.values(pentestCounts || {}).map((value) => num(value, 0)),
    ...Object.values(cveCounts || {}).map((value) => num(value, 0))
  );

  return (
    <SectionCard title="Severity and Exposure Mix" subtitle="Validated findings versus supporting CVE evidence across the engagement.">
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Validated findings
          </Typography>
          <SeverityCountPills counts={pentestCounts} prefix="pentest" />
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            CVE evidence
          </Typography>
          <SeverityCountPills counts={cveCounts} prefix="cves" />
        </Box>

        <Divider />

        <Stack spacing={1.4}>
          {Object.keys(severityMeta).map((key) => {
            const pentestValue = num(pentestCounts?.[key], 0);
            const cveValue = num(cveCounts?.[key], 0);
            const barColor = severityMeta[key].color;

            return (
              <Box key={key}>
                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
                  <Chip
                    size="small"
                    label={severityMeta[key].label}
                    sx={{
                      bgcolor: alpha(barColor, 0.1),
                      color: barColor,
                      fontWeight: 800,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Findings {pentestValue} - CVEs {cveValue}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(Math.max(pentestValue, cveValue) / maxValue) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: alpha(barColor, 0.08),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      bgcolor: barColor,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Stack>

        <Alert
          icon={false}
          sx={{
            borderRadius: 3,
            bgcolor: alpha(PRIMARYCOLOR, 0.05),
            color: '#23425d',
            border: '1px solid',
            borderColor: alpha(PRIMARYCOLOR, 0.09),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {totalPentest} validated findings and {totalCves} supporting CVE observations are shaping the current posture.
          </Typography>
        </Alert>
      </Stack>
    </SectionCard>
  );
};

const PentestDashboard = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [assetsData, setAssetsData] = useState([]);
  const [urlsData, setUrlsData] = useState([]);
  const [exploitsData, setExploitsData] = useState([]);
  const [threatModel, setThreatModel] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return undefined;
    }

    let active = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError('');

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [statsRes, assetsRes, urlsRes, exploitsRes, threatRes] = await Promise.all([
          fetch(`${BASEURL}/pentest/stats?pentest_id=${pentest_id}`, { headers }),
          fetch(`${BASEURL}/pentest/assets?pentest_id=${pentest_id}`, { headers }),
          fetch(`${BASEURL}/pentest/urls?pentest_id=${pentest_id}`, { headers }),
          fetch(`${BASEURL}/pentest/exploits?pentest_id=${pentest_id}`, { headers }),
          fetch(`${BASEURL}/pentests/external/threat_model?pentest_id=${pentest_id}`, { headers }),
        ]);

        const [statsJson, assetsJson, urlsJson, exploitsJson, threatJson] = await Promise.all([
          statsRes.json(),
          assetsRes.json(),
          urlsRes.json(),
          exploitsRes.json(),
          threatRes.json(),
        ]);

        const invalidToken = [statsJson, assetsJson, urlsJson, exploitsJson, threatJson].some(
          (payload) => payload?.error && payload?.message === 'Invalid token'
        );

        if (invalidToken) {
          navigate('/');
          return;
        }

        if (!active) return;

        setStats(statsJson?.stats || null);
        setAssetsData(Array.isArray(assetsJson?.assets) ? assetsJson.assets : []);
        setUrlsData(Array.isArray(urlsJson?.urls) ? urlsJson.urls : []);
        setExploitsData(Array.isArray(exploitsJson?.exploits) ? exploitsJson.exploits : []);
        setThreatModel(threatJson?.success ? threatJson?.threat_model || null : null);
      } catch (fetchError) {
        console.error(fetchError);
        if (!active) return;
        setError('Unable to load the external dashboard right now.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadDashboard();

    return () => {
      active = false;
    };
  }, [navigate, pentest_id]);

  const groupedAssets = useMemo(() => {
    const grouped = {};

    for (const asset of assetsData) {
      const host = asset?.host || 'Unknown host';
      if (!grouped[host]) {
        grouped[host] = {
          host,
          description: '',
          ports: new Set(),
          services: new Set(),
          technologies: new Set(),
        };
      }

      const description = String(asset?.description || '').trim();
      if (description && (!grouped[host].description || description.length > grouped[host].description.length)) {
        grouped[host].description = description;
      }

      safeParseList(asset?.ports).forEach((port) => grouped[host].ports.add(port));
      safeParseList(asset?.services).forEach((service) => grouped[host].services.add(service));
      safeParseList(asset?.technologies).forEach((technology) => grouped[host].technologies.add(technology));
    }

    return Object.values(grouped).map((entry) => ({
      host: entry.host,
      description: entry.description,
      ports: [...entry.ports].filter(Boolean),
      services: [...entry.services].filter(Boolean),
      technologies: [...entry.technologies].filter(Boolean),
    }));
  }, [assetsData]);

  const inventoryLookup = useMemo(() => {
    const map = new Map();
    groupedAssets.forEach((asset) => {
      const key = normalizeLookupKey(asset.host);
      if (key) map.set(key, asset);
    });
    return map;
  }, [groupedAssets]);

  const assetReconStats = useMemo(() => {
    const ports = new Map();
    const services = new Map();
    const technologies = new Map();

    groupedAssets.forEach((asset) => {
      asset.ports.forEach((port) => ports.set(String(port), (ports.get(String(port)) || 0) + 1));
      asset.services.forEach((service) => services.set(String(service), (services.get(String(service)) || 0) + 1));
      asset.technologies.forEach((technology) => {
        const normalized = normalizeTechToken(technology);
        if (!normalized) return;
        technologies.set(normalized, (technologies.get(normalized) || 0) + 1);
      });
    });

    return {
      uniqueHosts: groupedAssets.length,
      uniquePorts: ports.size,
      uniqueServices: services.size,
      uniqueTechnologies: technologies.size,
      topPorts: [...ports.entries()]
        .sort((left, right) => right[1] - left[1])
        .slice(0, 5)
        .map(([label, value]) => ({ label, value })),
      topServices: [...services.entries()]
        .sort((left, right) => right[1] - left[1])
        .slice(0, 5)
        .map(([label, value]) => ({ label, value })),
      topTechnologies: [...technologies.entries()]
        .sort((left, right) => right[1] - left[1])
        .slice(0, 5)
        .map(([label, value]) => ({ label, value })),
    };
  }, [groupedAssets]);

  const urlStats = useMemo(() => {
    const domains = new Map();
    let ok2xx = 0;
    let non2xx = 0;

    urlsData.forEach((row) => {
      const domain = getDomain(row?.url);
      domains.set(domain, (domains.get(domain) || 0) + 1);

      const status = num(row?.status, NaN);
      if (Number.isFinite(status) && status >= 200 && status < 300) ok2xx += 1;
      else non2xx += 1;
    });

    return {
      total: urlsData.length,
      uniqueDomains: domains.size,
      ok2xx,
      non2xx,
      healthyRate: urlsData.length ? ok2xx / urlsData.length : 0,
      topDomains: [...domains.entries()]
        .sort((left, right) => right[1] - left[1])
        .slice(0, 5)
        .map(([label, value]) => ({ label, value })),
    };
  }, [urlsData]);

  const exploitRows = useMemo(
    () =>
      exploitsData.map((row, index) => ({
        id: row?.id ?? `${row?.name || 'finding'}-${index}`,
        finding: row?.name || 'Unnamed finding',
        severity: severityKey(row?.severity),
        description: row?.description || '',
        impactedAssets: parseImpactedAssets(row?.impacted_assets),
      })),
    [exploitsData]
  );

  const pentestSeverity = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    exploitRows.forEach((row) => {
      counts[row.severity] += 1;
    });
    return counts;
  }, [exploitRows]);

  const findingsSummary = useMemo(() => {
    const map = new Map();

    exploitRows.forEach((row) => {
      const current = map.get(row.finding) || {
        label: row.finding,
        value: 0,
        affectedAssets: new Set(),
        highestSeverity: 'info',
      };

      current.value += 1;
      row.impactedAssets.forEach((asset) => current.affectedAssets.add(asset));
      if (severityMeta[row.severity].order > severityMeta[current.highestSeverity].order) {
        current.highestSeverity = row.severity;
      }

      map.set(row.finding, current);
    });

    return [...map.values()]
      .sort(
        (left, right) =>
          severityMeta[right.highestSeverity].order - severityMeta[left.highestSeverity].order ||
          right.value - left.value ||
          left.label.localeCompare(right.label)
      )
      .slice(0, 6)
      .map((item) => ({
        label: item.label,
        value: item.value,
        highestSeverity: item.highestSeverity,
        assetCount: item.affectedAssets.size,
      }));
  }, [exploitRows]);

  const topAffectedAssets = useMemo(() => {
    const map = new Map();

    exploitRows.forEach((row) => {
      row.impactedAssets.forEach((asset) => {
        const current = map.get(asset) || {
          asset,
          findings: 0,
          counts: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
        };
        current.findings += 1;
        current.counts[row.severity] += 1;
        map.set(asset, current);
      });
    });

    return [...map.values()]
      .map((entry) => {
        const exposureWeight = Object.entries(entry.counts).reduce(
          (sum, [key, value]) => sum + severityMeta[key].weight * num(value, 0),
          0
        );
        const highestSeverity =
          Object.keys(severityMeta).find((key) => num(entry.counts[key], 0) > 0) || 'info';
        const inventoryMatch = inventoryLookup.get(normalizeLookupKey(entry.asset));

        return {
          ...entry,
          exposureWeight,
          highestSeverity,
          portsCount: inventoryMatch?.ports?.length || 0,
          servicesCount: inventoryMatch?.services?.length || 0,
          techCount: inventoryMatch?.technologies?.length || 0,
        };
      })
      .sort(
        (left, right) =>
          right.exposureWeight - left.exposureWeight ||
          right.findings - left.findings ||
          left.asset.localeCompare(right.asset)
      )
      .slice(0, 7);
  }, [exploitRows, inventoryLookup]);

  const topImpactedAssetRows = useMemo(
    () =>
      topAffectedAssets.slice(0, 5).map((entry) => ({
        label: entry.asset,
        value: entry.findings,
        secondary: `${severityMeta[entry.highestSeverity].label} - ${entry.findings} finding${entry.findings === 1 ? '' : 's'}`,
      })),
    [topAffectedAssets]
  );

  const threatOverview = useMemo(() => {
    const endpoints = Array.isArray(threatModel?.endpoints) ? threatModel.endpoints : [];
    const endpointMeta = new Map();

    endpoints.forEach((endpoint) => {
      const attacks = Array.isArray(endpoint?.threat_model?.attacks)
        ? endpoint.threat_model.attacks
        : Array.isArray(endpoint?.attacks)
          ? endpoint.attacks
          : [];

      const ports = new Set();
      const services = new Set();
      const sev = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
      let findingsCount = 0;
      let likelyCount = 0;
      let queuedCount = 0;
      let maxScore = 0;

      attacks.forEach((attack) => {
        const attackScore = num(attack?.score, 0);
        if (attackScore >= 75) likelyCount += 1;
        if (attackScore >= VERIFY_THRESHOLD) queuedCount += 1;
        maxScore = Math.max(maxScore, attackScore);

        safeParseList(attack?.ports).forEach((port) => ports.add(String(port)));
        safeParseList(attack?.service).forEach((service) => services.add(String(service)));

        getAttackFindings(attack).forEach((finding) => {
          const key = severityKey(finding?.severity);
          sev[key] += 1;
          findingsCount += 1;
        });
      });

      endpointMeta.set(endpoint?.path || `Endpoint ${endpointMeta.size + 1}`, {
        path: endpoint?.path || `Endpoint ${endpointMeta.size + 1}`,
        maxScore,
        queuedCount,
        likelyCount,
        findingsCount,
        totalAttacks: attacks.length,
        portsCount: ports.size,
        servicesCount: services.size,
        sev,
      });
    });

    const scoreBins = { low: 0, mid: 0, high: 0, vhigh: 0 };
    const attackFamilies = new Map();
    let totalAttacks = 0;
    let totalFindings = 0;

    endpoints.forEach((endpoint) => {
      const attacks = Array.isArray(endpoint?.threat_model?.attacks)
        ? endpoint.threat_model.attacks
        : Array.isArray(endpoint?.attacks)
          ? endpoint.attacks
          : [];

      attacks.forEach((attack) => {
        totalAttacks += 1;
        const score = num(attack?.score, 0);
        if (score >= 75) scoreBins.vhigh += 1;
        else if (score >= 50) scoreBins.high += 1;
        else if (score >= VERIFY_THRESHOLD) scoreBins.mid += 1;
        else scoreBins.low += 1;

        const family = attack?.name || 'Unlabeled attack path';
        attackFamilies.set(family, (attackFamilies.get(family) || 0) + 1);

        getAttackFindings(attack).forEach(() => {
          totalFindings += 1;
        });
      });
    });

    const topEndpointsByRisk = [...endpointMeta.values()]
      .sort(
        (left, right) =>
          right.maxScore - left.maxScore ||
          right.likelyCount - left.likelyCount ||
          right.findingsCount - left.findingsCount ||
          left.path.localeCompare(right.path)
      )
      .slice(0, 6);

    return {
      totalEndpoints: endpoints.length,
      totalAttacks,
      totalQueued: scoreBins.mid + scoreBins.high + scoreBins.vhigh,
      likelyPaths: scoreBins.vhigh,
      totalFindings,
      endpointsWithFindings: [...endpointMeta.values()].filter((entry) => entry.findingsCount > 0).length,
      scoreBins,
      uniqueAttacks: attackFamilies.size,
      topEndpointsByRisk,
    };
  }, [threatModel]);

  const riskScore = Number.isFinite(Number(stats?.risk_score)) ? Number(stats?.risk_score) : null;
  const grade = getGrade(riskScore);
  const gradeColor = gradePalette[grade] || gradePalette['?'];

  const counts = stats?.counts || {};
  const cveSeverity = {
    critical: num(counts?.critical_cves, 0),
    high: num(counts?.high_cves, 0),
    medium: num(counts?.medium_cves, 0),
    low: num(counts?.low_cves, 0),
    info: num(counts?.info_cves, 0),
  };

  const riskNarrative = useMemo(() => {
    const highPriority = num(pentestSeverity.critical, 0) + num(pentestSeverity.high, 0);

    if (highPriority > 0) {
      return `${highPriority} critical or high-severity findings are currently driving risk across the external surface. Focus first on exposed hosts with repeated findings and the highest-confidence attack paths.`;
    }

    if (threatOverview.likelyPaths > 0) {
      return `${threatOverview.likelyPaths} likely attack paths are modeled across ${threatOverview.totalEndpoints} targets. Prioritize verification work against the highest-scoring endpoints and close repeat issues quickly.`;
    }

    if (exploitRows.length > 0) {
      return `${exploitRows.length} validated findings are currently recorded across ${groupedAssets.length} discovered hosts. Continue tightening exposed services and monitoring findings concentration by host.`;
    }

    if (groupedAssets.length > 0 || urlsData.length > 0) {
      return `Recon has identified ${groupedAssets.length} hosts and ${urlsData.length} URLs so far. Threat modeling and validation are in progress as the attack surface continues to mature.`;
    }

    return 'Assessment telemetry is still populating. Recon, threat modeling, and findings data will appear here as the engagement progresses.';
  }, [exploitRows.length, groupedAssets.length, pentestSeverity.critical, pentestSeverity.high, threatOverview, urlsData.length]);

  const needsAttention = num(pentestSeverity.critical, 0) + num(pentestSeverity.high, 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading external dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', bgcolor: lighten(PRIMARYCOLOR, 0.985) }}>
        <Nav />
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  if (!stats && !assetsData.length && !urlsData.length && !exploitsData.length) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', bgcolor: lighten(PRIMARYCOLOR, 0.985) }}>
        <Nav />
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            No dashboard data is available for this engagement yet.
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: lighten(PRIMARYCOLOR, 0.985) }}>
      <Nav />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflowY: 'auto' }}>
        <Stack spacing={2.5}>
          <SectionCard
            title="External Pentest Dashboard"
            subtitle="Assessment coverage, attack modeling, and validated exposure across the discovered external attack surface."
            action={
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<ArrowOutwardRoundedIcon />}
                  onClick={() => navigate(`/pentest/threat_model/${pentest_id}`)}
                  sx={{ borderRadius: 999 }}
                >
                  Threat Model
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<ArrowOutwardRoundedIcon />}
                  onClick={() => navigate(`/pentest/findings/${pentest_id}`)}
                  sx={{ borderRadius: 999 }}
                >
                  Findings
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<ArrowOutwardRoundedIcon />}
                  onClick={() => navigate(`/pentest/assets/${pentest_id}`)}
                  sx={{ borderRadius: 999 }}
                >
                  Recon
                </Button>
              </Stack>
            }
            sx={{ p: 2.6 }}
          >
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Chip
                label={`${groupedAssets.length} discovered host${groupedAssets.length === 1 ? '' : 's'}`}
                sx={{ fontWeight: 700, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }}
              />
              <Chip
                label={`${urlStats.uniqueDomains} domain${urlStats.uniqueDomains === 1 ? '' : 's'} in web surface`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                label={`${threatOverview.totalAttacks} modeled attack path${threatOverview.totalAttacks === 1 ? '' : 's'}`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                label={`${exploitRows.length} validated finding${exploitRows.length === 1 ? '' : 's'}`}
                sx={{ fontWeight: 700 }}
              />
            </Stack>
          </SectionCard>

          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Card sx={{ ...cardShellSx, p: 3, height: '100%' }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.4} alignItems="center">
                    <Box
                      sx={{
                        width: 54,
                        height: 54,
                        borderRadius: 3,
                        bgcolor: alpha(gradeColor, 0.1),
                        color: gradeColor,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ShieldOutlinedIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Risk Posture
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 900, color: '#10243E', lineHeight: 1.05 }}>
                        Grade {grade}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="flex-end">
                    <Typography variant="h1" sx={{ fontWeight: 950, color: gradeColor, lineHeight: 0.9 }}>
                      {grade}
                    </Typography>
                    <Box sx={{ pb: 0.6 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Assessment score
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {riskScore ?? 'N/A'} / 100
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography variant="body1" sx={{ color: '#20354d', lineHeight: 1.7 }}>
                    {riskNarrative}
                  </Typography>

                  <LinearProgress
                    variant="determinate"
                    value={Math.max(0, Math.min(100, num(riskScore, 0)))}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      bgcolor: alpha(gradeColor, 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        bgcolor: gradeColor,
                      },
                    }}
                  />

                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip
                      icon={<WarningAmberRoundedIcon sx={{ color: '#d32f2f !important' }} />}
                      label={`${needsAttention} high-priority issue${needsAttention === 1 ? '' : 's'}`}
                      sx={{
                        fontWeight: 800,
                        bgcolor: alpha('#d32f2f', 0.08),
                        color: '#d32f2f',
                      }}
                    />
                    <Chip
                      label={`${threatOverview.likelyPaths} likely path${threatOverview.likelyPaths === 1 ? '' : 's'}`}
                      sx={{ fontWeight: 800, bgcolor: alpha('#ef6c00', 0.08), color: '#ef6c00' }}
                    />
                    <Chip
                      label={`${topAffectedAssets.length} affected asset${topAffectedAssets.length === 1 ? '' : 's'}`}
                      sx={{ fontWeight: 800 }}
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {riskScore != null
                      ? `${riskScore} / 100 score recorded for this engagement, based on the current findings, supporting evidence, and observed attack surface.`
                      : 'A score will appear here once enough assessment telemetry has been collected.'}
                  </Typography>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} lg={4}>
                  <MetricCard
                    label="Validated Findings"
                    value={exploitRows.length}
                    caption={`${needsAttention} critical/high issues currently need attention`}
                    icon={BugReportOutlinedIcon}
                    tone={needsAttention > 0 ? 'red' : 'blue'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <MetricCard
                    label="Likely Attack Paths"
                    value={threatOverview.likelyPaths}
                    caption={`${threatOverview.totalQueued} paths are above the verification threshold`}
                    icon={AccountTreeOutlinedIcon}
                    tone={threatOverview.likelyPaths > 0 ? 'amber' : 'blue'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <MetricCard
                    label="Discovered Hosts"
                    value={assetReconStats.uniqueHosts}
                    caption={`${assetReconStats.uniquePorts} unique ports and ${assetReconStats.uniqueServices} services observed`}
                    icon={StorageOutlinedIcon}
                    tone="blue"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <MetricCard
                    label="URLs in Scope"
                    value={urlStats.total}
                    caption={`${urlStats.uniqueDomains} unique domains with ${formatPct(urlStats.healthyRate)} returning 2xx`}
                    icon={LinkOutlinedIcon}
                    tone="green"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <MetricCard
                    label="Open Ports"
                    value={num(counts?.open_ports, assetReconStats.uniquePorts)}
                    caption={`${assetReconStats.topPorts[0]?.label || 'No dominant port'} is the most common service edge`}
                    icon={LanOutlinedIcon}
                    tone="slate"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <MetricCard
                    label="Domains and Targets"
                    value={Math.max(urlStats.uniqueDomains, threatOverview.totalEndpoints)}
                    caption={`${threatOverview.totalEndpoints} threat-modeled target${threatOverview.totalEndpoints === 1 ? '' : 's'} in active review`}
                    icon={PublicOutlinedIcon}
                    tone="purple"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <SectionCard
                title="Attack Verification Flow"
                subtitle="From modeled hypotheses through likely attack paths and confirmed findings."
                action={
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => navigate(`/pentest/threat_model/${pentest_id}`)}
                  >
                    Open threat model
                  </Button>
                }
              >
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <FlowStat
                      label="Hypotheses"
                      value={threatOverview.totalAttacks}
                      helper={`${threatOverview.uniqueAttacks} distinct attack families modeled`}
                      tone="blue"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <FlowStat
                      label="Queued"
                      value={threatOverview.totalQueued}
                      helper={`Score ${VERIFY_THRESHOLD}+ and ready for verification`}
                      tone="slate"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <FlowStat
                      label="Likely"
                      value={threatOverview.likelyPaths}
                      helper="High-confidence paths that deserve immediate review"
                      tone="amber"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <FlowStat
                      label="Confirmed"
                      value={threatOverview.totalFindings}
                      helper={`${threatOverview.endpointsWithFindings} target${threatOverview.endpointsWithFindings === 1 ? '' : 's'} currently have findings`}
                      tone="red"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Stack spacing={1.4}>
                      {scoreBreakpoints.map((row) => {
                        const total = Math.max(1, threatOverview.totalAttacks);
                        const value = num(threatOverview.scoreBins[row.key], 0);
                        return (
                          <Box key={row.key}>
                            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.6 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#10243E' }}>
                                {row.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {value} / {total}
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={(value / total) * 100}
                              sx={{
                                height: 8,
                                borderRadius: 999,
                                bgcolor: alpha(row.color, 0.1),
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 999,
                                  bgcolor: row.color,
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: alpha(PRIMARYCOLOR, 0.04),
                        border: '1px solid',
                        borderColor: alpha(PRIMARYCOLOR, 0.08),
                        height: '100%',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                        Current Assessment Read
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          Threat modeling is populated across {threatOverview.totalEndpoints} targets with {threatOverview.totalAttacks} total attack paths under review.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {threatOverview.totalQueued} attack paths are at or above the verify threshold and {threatOverview.likelyPaths} already score in the likely range.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          Validated findings are currently recorded on {threatOverview.endpointsWithFindings} target{threatOverview.endpointsWithFindings === 1 ? '' : 's'}.
                        </Typography>
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            <Grid item xs={12} lg={5}>
              <RiskBreakdownCard pentestCounts={pentestSeverity} cveCounts={cveSeverity} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <SectionCard
                title="Top At-Risk Assets"
                subtitle="Assets with the highest concentration of validated findings and repeated exposure."
                action={
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => navigate(`/pentest/findings/${pentest_id}`)}
                  >
                    Open findings
                  </Button>
                }
              >
                {topAffectedAssets.length ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800 }}>Asset</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Exposure</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Findings</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Inventory</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topAffectedAssets.map((row) => {
                          const maxExposure = Math.max(1, ...topAffectedAssets.map((entry) => entry.exposureWeight));
                          return (
                            <TableRow key={row.asset} hover>
                              <TableCell sx={{ maxWidth: 220 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#10243E' }} noWrap title={row.asset}>
                                  {row.asset}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Highest severity: {severityMeta[row.highestSeverity].label}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ minWidth: 170 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={(row.exposureWeight / maxExposure) * 100}
                                  sx={{
                                    height: 8,
                                    borderRadius: 999,
                                    bgcolor: alpha(severityMeta[row.highestSeverity].color, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 999,
                                      bgcolor: severityMeta[row.highestSeverity].color,
                                    },
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: 'block' }}>
                                  Exposure index {row.exposureWeight}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={row.findings}
                                  sx={{
                                    fontWeight: 800,
                                    bgcolor: alpha(severityMeta[row.highestSeverity].color, 0.1),
                                    color: severityMeta[row.highestSeverity].color,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {row.portsCount} ports - {row.servicesCount} services - {row.techCount} tech
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Validated findings will surface the highest-risk assets here once impacted locations are available.
                  </Typography>
                )}
              </SectionCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <SectionCard
                title="Likely Attack Paths"
                subtitle="Highest-scoring targets from the threat model, ranked by confidence and observed findings."
                action={
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => navigate(`/pentest/threat_model/${pentest_id}`)}
                  >
                    Review targets
                  </Button>
                }
              >
                {threatOverview.topEndpointsByRisk.length ? (
                  <Stack spacing={1.25}>
                    {threatOverview.topEndpointsByRisk.map((endpoint) => {
                      const scoreColor = endpoint.maxScore >= 75 ? '#d32f2f' : endpoint.maxScore >= 50 ? '#ef6c00' : PRIMARYCOLOR;
                      return (
                        <Box
                          key={endpoint.path}
                          sx={{
                            p: 1.8,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: alpha(scoreColor, 0.12),
                            bgcolor: alpha(scoreColor, 0.03),
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.8 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#10243E', minWidth: 0 }} noWrap title={endpoint.path}>
                              {endpoint.path}
                            </Typography>
                            <Chip
                              size="small"
                              label={`Score ${endpoint.maxScore}`}
                              sx={{
                                fontWeight: 800,
                                bgcolor: alpha(scoreColor, 0.12),
                                color: scoreColor,
                              }}
                            />
                          </Stack>

                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, endpoint.maxScore)}
                            sx={{
                              height: 8,
                              borderRadius: 999,
                              bgcolor: alpha(scoreColor, 0.08),
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 999,
                                bgcolor: scoreColor,
                              },
                            }}
                          />

                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1 }}>
                            <Chip size="small" label={`${endpoint.totalAttacks} paths`} />
                            <Chip size="small" label={`${endpoint.queuedCount} queued`} />
                            <Chip size="small" label={`${endpoint.findingsCount} findings`} />
                            <Chip size="small" label={`${endpoint.portsCount} ports - ${endpoint.servicesCount} services`} />
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Threat-model targets will appear here as soon as attack paths are generated.
                  </Typography>
                )}
              </SectionCard>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <SectionCard
                title="Recon Coverage"
                subtitle="Discovered hosts, domains, ports, services, and technologies from the current recon set."
                action={
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => navigate(`/pentest/urls/${pentest_id}`)}
                  >
                    Review recon
                  </Button>
                }
              >
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha(PRIMARYCOLOR, 0.06) }}>
                      <Typography variant="caption" color="text.secondary">
                        Hosts
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {assetReconStats.uniqueHosts}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha(PRIMARYCOLOR, 0.06) }}>
                      <Typography variant="caption" color="text.secondary">
                        Domains
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {urlStats.uniqueDomains}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha('#2e7d32', 0.06) }}>
                      <Typography variant="caption" color="text.secondary">
                        2xx URLs
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {urlStats.ok2xx}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ p: 1.7, borderRadius: 3, bgcolor: alpha('#ef6c00', 0.06) }}>
                      <Typography variant="caption" color="text.secondary">
                        Non-2xx URLs
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E' }}>
                        {urlStats.non2xx}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                      Top Ports
                    </Typography>
                    <BarList rows={assetReconStats.topPorts} color={PRIMARYCOLOR} emptyLabel="No open ports recorded yet." />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                      Top Domains
                    </Typography>
                    <BarList rows={urlStats.topDomains} color="#2e7d32" emptyLabel="No URL domains recorded yet." />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                      Services
                    </Typography>
                    <BarList rows={assetReconStats.topServices} color="#455a64" emptyLabel="No service inventory recorded yet." />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                      Technologies
                    </Typography>
                    <BarList rows={assetReconStats.topTechnologies} color="#7b1fa2" emptyLabel="No technology fingerprints recorded yet." />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <SectionCard
                title="Findings Spotlight"
                subtitle="Repeated finding families and the assets absorbing the most validated exposure."
                action={
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => navigate(`/pentest/findings/${pentest_id}`)}
                  >
                    Investigate findings
                  </Button>
                }
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                  Most Repeated Finding Families
                </Typography>

                {findingsSummary.length ? (
                  <Stack spacing={1.2}>
                    {findingsSummary.map((row) => (
                      <Box
                        key={row.label}
                        sx={{
                          p: 1.7,
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: alpha(severityMeta[row.highestSeverity].color, 0.12),
                          bgcolor: alpha(severityMeta[row.highestSeverity].color, 0.035),
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#10243E', minWidth: 0 }} noWrap title={row.label}>
                            {row.label}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${row.value}x`}
                            sx={{
                              fontWeight: 800,
                              bgcolor: alpha(severityMeta[row.highestSeverity].color, 0.12),
                              color: severityMeta[row.highestSeverity].color,
                            }}
                          />
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1 }}>
                          <Chip size="small" label={severityMeta[row.highestSeverity].label} />
                          <Chip size="small" label={`${row.assetCount} affected asset${row.assetCount === 1 ? '' : 's'}`} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Finding families will appear here once validated findings are available.
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1.2 }}>
                  Blast Radius
                </Typography>
                <BarList rows={topImpactedAssetRows} color="#d32f2f" emptyLabel="Impacted assets will populate here as finding locations are recorded." secondaryKey="secondary" />
              </SectionCard>
            </Grid>
          </Grid>

          {needsAttention > 0 ? (
            <Alert
              icon={<HubOutlinedIcon fontSize="inherit" />}
              sx={{
                borderRadius: 3,
                bgcolor: alpha('#d32f2f', 0.05),
                color: '#7a1f1f',
                border: '1px solid',
                borderColor: alpha('#d32f2f', 0.12),
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Critical and high-severity issues are concentrated across {topAffectedAssets.length || 0} asset{topAffectedAssets.length === 1 ? '' : 's'}.
                Start with the top-risk assets and highest-scoring threat-model targets to reduce the largest immediate exposure.
              </Typography>
            </Alert>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};

export default PentestDashboard;
